import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, generatePath, useLocation } from 'react-router-dom';
import { useAuthContext } from 'contexts/AuthContext';
import CreateProFormaModal from 'views/CreateProFormaModal/CreateProFormaModal';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import { useGetProFormasByDeal } from 'queries/ProForma';
import { useGetUploadedProFormasByDeal } from 'queries/UploadedProForma';
import { useGetExtractedProFormasByDeal } from 'queries/ExtractedProForma';
import { IOptionAnyValue } from 'bricks/types';
import { useGetBackOfEnvelopeScenarios } from 'views/DealDashboard/BOE/hooks/useGetBOEScenarios';
import PageTitle from '../../components/PageTitle/PageTitle';
import RenderIf from '../../components/RenderIf/RenderIf';
import { NSButton } from '../../bricks';
import { URLS } from '../../services/urls';
import ProFormaTableTabs from './components/ProFormaTableTabs/ProFormaTableTabs';

export interface IProFormaHomeProps {
    dealId?: number;
}

const ProFormaHome = () => {
    const { selectedOrganizationId } = useAuthContext();
    const { dealId } = useParams<{ dealId: string }>();
    const navigate = useNavigate();
    const uploadPath = generatePath(URLS.PRO_FORMA.UPLOAD, { dealId });

    const [openCreateProFormaModal, setOpenCreateProFormaModal] = useState(false);
    const [initialScenario, setInitialScenario] = useState<IOptionAnyValue | undefined>(undefined);
    
    const location = useLocation();
    // If navigation state requests opening the create modal, open it once on mount
    useEffect(() => {
        if (location?.state && location.state.openCreateProForma) {
            setOpenCreateProFormaModal(true);
            window.history.replaceState(
                { ...window.history.state, usr: { ...location.state, openCreateProForma: undefined } },
                '',
            );
        }
    }, [location]);

    const { data: fetchedDeal, isLoading } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const { data: northspyreProFormas, isLoading: isLoadingNorthspyreProFormas } = useGetProFormasByDeal({ dealId: Number(dealId) });
    const { data: uploadedProFormas, isLoading: isLoadingUploadedProFormas } = useGetUploadedProFormasByDeal({
        organizationId: selectedOrganizationId as string,
        dealId: Number(dealId),
    });
    const { data: extractedProFormas } = useGetExtractedProFormasByDeal({
        organizationId: selectedOrganizationId as string,
        dealId: Number(dealId),
    });
    const successfulExtractions = useMemo(
        () => (extractedProFormas ?? []).filter(run => run.extractionStatus === 'SUCCEEDED'),
        [extractedProFormas],
    );
    const toggleOpenProformaModal = () => setOpenCreateProFormaModal(!openCreateProFormaModal);

    const {
        data: { items: boeScenarios } = {},
        isLoading: areScenariosLoading,
         
    } = useGetBackOfEnvelopeScenarios({ orgId: selectedOrganizationId!, dealId: Number(dealId) });

    useEffect(() => {
        if (boeScenarios?.length) setInitialScenario({ label: boeScenarios[0].name, value: boeScenarios[0].id });
        else setInitialScenario(undefined);
    }, [boeScenarios]);

    if (!fetchedDeal && !isLoading && !isLoadingNorthspyreProFormas) {
        return <div>No deal selected</div>;
    }  

    return (
        <>
            <PageTitle
                title="Pro Forma"
                displayBreadcrumb
                dealName={fetchedDeal?.dealName}
                pageMetaDataTitle="Pro Forma Home"
                containerClassName="mb-3"
                beta
                betaCustomText="Beta"
            >
                <NSButton
                    color="secondary"
                    outline={false}
                    callback={() => navigate(uploadPath)}
                    text="Upload"
                    className="mr-2 heap-deal-pro-forma-home-upload-pro-forma"
                />
                <NSButton
                    color="primary"
                    id="create-pro-forma"
                    className="heap-deal-pro-forma-home-create-pro-forma"
                    outline={false}
                    callback={toggleOpenProformaModal}
                    text="Create pro forma"
                />
            </PageTitle>

            <RenderIf
                isTrue={!isLoading && !isLoadingNorthspyreProFormas && !isLoadingUploadedProFormas}
                isLoading={isLoading || isLoadingNorthspyreProFormas || isLoadingUploadedProFormas}
            >
                <ProFormaTableTabs
                    proFormas={northspyreProFormas || []}
                    uploadedProFormas={uploadedProFormas || []}
                    extractedProFormas={successfulExtractions}
                    isLoadingProFormas={isLoadingNorthspyreProFormas}
                    isLoadingUploadedProFormas={isLoadingUploadedProFormas}
                    emptyStateSubtitle="Create your first pro forma from scratch or upload your own."
                />
            </RenderIf>

            <RenderIf isTrue={openCreateProFormaModal && !areScenariosLoading}>
                <CreateProFormaModal
                    toggle={toggleOpenProformaModal}
                    isOpen={openCreateProFormaModal}
                    initialScenario={initialScenario}
                    dealAssetTypeCorrelationId={fetchedDeal?.assetType?.correlationId || ''}
                />
            </RenderIf>
        </>
    );
};

export default ProFormaHome;
